import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import ButtonArea from "./button-area";
import theme from "../../theme";
import { Link } from "react-router-dom";
import SpinnerIcon from "../spinner-icon";

const Button = ({
    text,
    icon,
    colorVariant,
    to,
    href,
    buttonRef,
    isLoading,
    ...props
}) => {
    let ButtonComponent;
    if (to) ButtonComponent = StyledLinkButton;
    else if (href) ButtonComponent = StyledExtLinkButton;
    else ButtonComponent = StyledButton;
    return (
        <StyledWrapper ref={buttonRef} $colorVariant={colorVariant}>
            <ButtonComponent
                {...props}
                to={to}
                href={href}
                $colorVariant={colorVariant}
            >
                {icon && (
                    <StyledIconWrapper $iconOnly={!!icon && text === null}>
                        {isLoading ? <SpinnerIcon /> : icon}
                    </StyledIconWrapper>
                )}
                {text}
            </ButtonComponent>
        </StyledWrapper>
    );
};

Button.propTypes = {
    text: PropTypes.string.isRequired,
    icon: PropTypes.node,
    colorVariant: PropTypes.oneOf(["default", "decline", "negative"]),
    to: PropTypes.string,
    href: PropTypes.string,
    isLoading: PropTypes.bool,
    buttonRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    ]),
};

Button.defaultProps = {
    icon: null,
    variant: "primary",
    colorVariant: "default",
    to: null,
    href: null,
    buttonRef: null,
    isLoading: false,
};

Button.Area = ButtonArea;
export default Button;

/* Helpers */

const COLORS = {
    default: {
        color: theme.buttonDefaultColor,
        background: theme.buttonDefaultBackground,
        hoverBackground: theme.buttonDefaultHoverBackground,
        disabledColor: theme.buttonDefaultDisabledColor,
        disabledBackground: theme.buttonDefaultDisabledBackground,
    },
    decline: {
        color: theme.buttonDeclineColor,
        background: theme.buttonDeclineBackground,
        hoverBackground: theme.buttonDeclineHoverBackground,
        disabledColor: theme.buttonDeclineDisabledColor,
        disabledBackground: theme.buttonDeclineDisabledBackground,
    },
    negative: {
        color: theme.buttonNegativeColor,
        background: theme.buttonNegativeBackground,
        hoverBackground: theme.buttonNegativeHoverBackground,
        disabledColor: theme.buttonNegativeDisabledColor,
        disabledBackground: theme.buttonNegativeDisabledBackground,
    },
};

/* Styled Components */

const StyledWrapper = styled.div`
    color: ${(props) => COLORS[props.$colorVariant].color};
    @media only screen and (max-width: 500px) {
        width: 100%;
    }
`;

const buttonBaseStyle = (props) => `
    cursor: pointer;
    padding: ${props.theme.buttonPadding};
    line-height: 23px;
    font-size: ${props.theme.buttonFontSize};
    font-weight: ${props.theme.buttonFontWeight};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background-color 150ms ease-in, color 150ms ease-in;
    border-radius: ${props.theme.buttonBorderRadius};
    box-shadow: ${props.theme.buttonShadow};
    :disabled {
        cursor: not-allowed;
    }
    @media only screen and (min-width: 1980px) {
        font-size: ${props.theme.buttonFontSizeHiRes};
    }
`;

const linkBaseStyle = () => `
    text-decoration: none;
`;

const primaryStyle = (props) => `
    width: 100%;
    ${buttonBaseStyle(props)}
    background-color: ${COLORS[props.$colorVariant].background};
    color: #fff;
    :hover:not(:disabled) {
        background-color: ${COLORS[props.$colorVariant].hoverBackground};
        box-shadow: none;
    }
    :disabled {
        background-color: ${COLORS[props.$colorVariant].disabledBackground};
        color: ${COLORS[props.$colorVariant].disabledColor};
        box-shadow: none;
    }
`;

const StyledButton = styled.button.attrs({ className: "btn" })`
    ${(props) => primaryStyle(props)}
`;

const StyledLinkButton = styled(Link)`
    ${(props) => primaryStyle(props)}
    ${(props) => linkBaseStyle(props)}
`;

const StyledExtLinkButton = styled.a`
    ${(props) => primaryStyle(props)}
    ${(props) => linkBaseStyle(props)}
`;

const StyledIconWrapper = styled.div`
    width: 1.25em;
    height: 1.25em;
    margin: ${(props) => (props.$iconOnly ? "0.75em" : "0 0.75em 0 0")};
    display: flex;
    align-items: center;

    svg {
        width: 1.25em;
        height: 1.25em;
    }
`;
